import type { CustomerCompany, SupportPlan } from "@sd/types/customerCompany";

function hueFromString(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = s.charCodeAt(i) + ((h << 5) - h);
  }
  return Math.abs(h) % 360;
}

function unwrapSelectLike(v: unknown): unknown {
  if (v && typeof v === "object" && !Array.isArray(v) && "value" in v) {
    return (v as { value: unknown }).value;
  }
  return v;
}

function toSupportPlanValue(v: unknown): SupportPlan {
  v = unwrapSelectLike(v);
  if (Array.isArray(v)) {
    if (v.length === 0) {
      return "free";
    }
    const joined = v.map(String).join(" ").toLowerCase();
    return joined.includes("paid") ? "paid" : "free";
  }
  if (typeof v === "string") {
    const t = v.trim().toLowerCase();
    if (t === "") {
      return "free";
    }
    return t.includes("paid") ? "paid" : "free";
  }
  if (typeof v === "number") {
    if (v === 1) {
      return "paid";
    }
    if (v === 0) {
      return "free";
    }
    return "free";
  }
  return "free";
}

function isEmptyPlanRaw(v: unknown): boolean {
  if (v === undefined || v === null) {
    return true;
  }
  if (Array.isArray(v) && v.length === 0) {
    return true;
  }
  if (typeof v === "string" && v.trim() === "") {
    return true;
  }
  return false;
}

/** Read support plan from grid/API row (Select scalar, Checkbox.Group array, or labelInValue object). */
function supportPlanFromRaw(raw: Record<string, unknown>): SupportPlan {
  const keys = [
    "support_plan",
    "supportplan",
    "plan",
    "support_tier",
    "supportPlan",
  ] as const;
  for (const k of keys) {
    if (!(k in raw)) {
      continue;
    }
    const v = raw[k];
    if (isEmptyPlanRaw(v)) {
      continue;
    }
    return toSupportPlanValue(v);
  }
  return "free";
}

/**
 * Formily Checkbox.Group submits `supportplan` as string[]; Select submits scalar.
 * APIs usually want `"paid"` | `"free"`.
 */
export function normalizeCompanyPayloadSupportPlanForApi(
  values: Record<string, unknown>
): Record<string, unknown> {
  const next = { ...values };
  const keys = ["support_plan", "supportplan"] as const;
  let resolved: SupportPlan | undefined;
  for (const key of keys) {
    if (!(key in next)) {
      continue;
    }
    const v = next[key];
    if (isEmptyPlanRaw(v)) {
      continue;
    }
    resolved = toSupportPlanValue(v);
    break;
  }
  if (resolved === undefined) {
    return next;
  }
  // Many backends persist only `supportplan` (legacy) or only `support_plan`.
  // Always send both so updates are not dropped when the form / serializer uses one key.
  next.support_plan = resolved;
  next.supportplan = resolved;
  return next;
}

/** Map Formily grid/detail/create payload into list row shape (best-effort field keys). */
export function mapFormilyToCustomerCompany(
  raw: Record<string, unknown>
): CustomerCompany {
  const pickStr = (keys: string[]): string => {
    for (const k of keys) {
      const v = raw[k];
      if (v !== undefined && v !== null && String(v).trim() !== "") {
        return String(v);
      }
    }
    return "";
  };
  const id = String(raw.id ?? raw.pk ?? "");
  const name =
    pickStr([
      "companyname",
      "company_name",
      "name",
      "title",
      "organization_name",
      "org_name",
    ]) || "Company";
  const domain = pickStr([
    "domain_name",
    "domain",
    "company_domain",
    "website",
    "web",
  ]);
  const contactName = pickStr([
    "contactname",
    "contact_name",
    "contact_person",
    "primary_contact",
    "contact",
  ]);
  const contactPhone = pickStr([
    "contactphone",
    "contact_phone",
    "phone",
    "mobile",
    "telephone",
  ]);
  const supportPlan = supportPlanFromRaw(raw);
  let registeredEmails: string[] = [];
  const emailsVal =
    raw.registeredemails ??
    raw.registered_emails ??
    raw.emails ??
    raw.email_addresses;
  if (Array.isArray(emailsVal)) {
    registeredEmails = emailsVal.map(String).filter(Boolean);
  } else if (typeof emailsVal === "string") {
    registeredEmails = emailsVal
      .split(/[\n,;]/)
      .map(s => s.trim())
      .filter(Boolean);
  }
  registeredEmails = [...new Set(registeredEmails)];
  const stableId = id || `tmp-${name}`;
  return {
    id: id || stableId,
    name,
    domain,
    registeredEmails,
    members: [],
    supportPlan,
    contactName: contactName || "—",
    contactPhone: contactPhone || "—",
    contractStart: new Date().toISOString().split("T")[0],
    monthlyTickets: Number(raw.monthly_tickets ?? raw.monthlyTickets ?? 0) || 0,
    activeTickets: Number(raw.active_tickets ?? raw.activeTickets ?? 0) || 0,
    csatAvg: Number(raw.csat_avg ?? raw.csat ?? raw.csatAvg ?? 0) || 0,
    logoColor: `${hueFromString(stableId)} 70% 50%`,
  };
}

/** After grid refetch, keep locally edited `members` / stable avatar color when ids match. */
export function mergeCompaniesFromGrid(
  next: CustomerCompany[],
  prev: CustomerCompany[]
): CustomerCompany[] {
  const prevById = new Map(prev.map(c => [c.id, c]));
  return next.map(c => {
    const old = prevById.get(c.id);
    if (old !== undefined && old.members.length > 0) {
      return { ...c, members: old.members, logoColor: old.logoColor };
    }
    return c;
  });
}
